import { Injectable } from '@angular/core';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { IndexedDbService } from '../../shared/services/indexedDb.service';
import { flatMap, map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Observable, of } from 'rxjs';
import { AppConstants } from '../../utils/appConstants.class';
import { DataService } from '../../shared/services';
import { InspectionPlugin, Asset } from '../../shared/models';

@Injectable()
export class AttachementsPluginFactoryService extends InspectionFactoryService {
  constructor(indexedDb: IndexedDbService, dataService: DataService) {
    super(indexedDb, dataService);
  }

  public addFiles(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    files: any,
    attachments?: any[]
  ): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            const assetValues = [];
            let counter = 0;
            plugin.config.AttachementDocuments = attachments ?? plugin.config.AttachementDocuments;
            files.forEach((file) => {
              const assetId = uuid();
              plugin.config.AttachementDocuments.push({
                AssetId: assetId,
                MimeType: file.type,
                Name: file.name,
                Url: `/assets/${assetId}/content`,
                FileTitle: file.fileTitle
              });
              assetValues.push({ assetId, key: `assets[${counter}]` });
              counter++;
            });
            return { plugin, assetValues };
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          const obs = [];
          const assetValues = [];
          let counter = 0;
          plugin.config.AttachementDocuments = attachments ?? plugin.config.AttachementDocuments;
          if (!inspection.assets) {
            inspection.assets = [];
          }
          files.forEach((file) => {
            const assetId = uuid();
            plugin.config.AttachementDocuments.push({
              AssetId: assetId,
              MimeType: file.type,
              Name: file.name,
              Url: `/assets/${assetId}/content`,
              FileTitle: file.fileTitle
            });
            if (this.isInspectionInOffline(inspectionId)) {
              const index = inspection.assets.findIndex((x: Asset) => x.assetId === assetId);
              if (index >= 0) {
                inspection.assets[index] = new Asset(assetId, file.name, file.type);
              } else {
                inspection.assets.push(new Asset(assetId, file.name, file.type));
              }

              obs.push(this.indexedDb.putImage(assetId, file, true));
            }
            assetValues.push({ assetId, key: `assets[${counter}]` });
            counter++;
          });
          plugin.etag = new Date();
          this.updateInspection(inspection);
          if (this.isInspectionInOffline(inspectionId)) {
            return Observable.zip(...obs).pipe(
              map((res) => {
                return { plugin, assetValues };
              })
            );
          }
          return of({ plugin, assetValues });
        }
        return of(null);
      })
    );
  }

  public deleteFile(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string
  ): Observable<InspectionPlugin> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            plugin.config.AttachementDocuments = plugin.config.AttachementDocuments.filter(
              (a) => a.AssetId !== assetId
            );
            return plugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          plugin.config.AttachementDocuments = plugin.config.AttachementDocuments.filter((a) => a.AssetId !== assetId);
          inspection.assets = inspection.assets?.filter((a) => a.assetId !== assetId);
          plugin.etag = new Date();
          this.updateInspection(inspection);
          return this.indexedDb.remove(AppConstants.INDEXEDDB_TABLES.images, assetId).pipe(
            map((res) => {
              return plugin;
            })
          );
        }
        return of(null);
      })
    );
  }

  public deleteFiles(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetIds: string[]
  ): Observable<InspectionPlugin> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            plugin.config.AttachementDocuments = plugin.config.AttachementDocuments.filter(
              (a) => !assetIds.includes(a.AssetId)
            );
            return plugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          const obs = [];
          plugin.config.AttachementDocuments = plugin.config.AttachementDocuments.filter(
            (a) => !assetIds.includes(a.AssetId)
          );
          if (this.isInspectionInOffline(inspectionId)) {
            inspection.assets = inspection.assets?.filter((a) => !assetIds.includes(a.assetId));
            assetIds.forEach((assetId) => {
              obs.push(this.indexedDb.remove(AppConstants.INDEXEDDB_TABLES.images, assetId));
            });
          }
          plugin.etag = new Date();
          this.updateInspection(inspection);
          if (this.isInspectionInOffline(inspectionId)) {
            return Observable.zip(...obs).pipe(
              map((res) => {
                return plugin;
              })
            );
          }
          return of(plugin);
        }
        return of(null);
      })
    );
  }
}
